import * as React from 'react';
import { useLocation, useSearch, useRouteContext, useNavigate } from '@tanstack/react-router';
import { AppFrame, Link, useNavAccess } from '@ooh/screens';
import { pickSharedFilterSearch } from '../lib/filter-config';
import {
  decodeCatalogSearch,
  decodeCatalogSearchViewBbox,
  extractViewBbox,
} from '../lib/catalog-return-context';
import { NotificationBell } from '../features/notifications/components/notification-bell';
import { changeLanguage } from '../i18n';

// ─── Props ────────────────────────────────────────────────────

interface AppShellProps {
  children: React.ReactNode;
  pageTitle?: string;
}

/** Routes for which shared filter params should be carried via the nav link. */
const FILTER_CARRY_PATHS = new Set(['/map', '/media-units']);

// --co-map-header-offset tells the client offer review map panel
// (packages/screens client-offer-review-map-panel.tsx) how much header space sits above this
// scroll container, so its sticky height calc can stay viewport-bounded here
// while defaulting to 0 on the public offer route, which renders without AppShell.
// eslint-disable-next-line i18next/no-literal-string -- CSS custom property, not user-facing
const MAIN_STYLE = { '--co-map-header-offset': 'var(--app-shell-header-h)' } as React.CSSProperties;

// ─── Component ────────────────────────────────────────────────

export function AppShell({ children, pageTitle = '' }: AppShellProps) {
  const [collapsed, setCollapsed] = React.useState(true);
  const { auth } = useRouteContext({ from: '__root__' });
  const navigate = useNavigate();
  const access = useNavAccess();
  const location = useLocation();
  const pathname = location.pathname;
  const activePath = pathname.replace(/\/$/, '') || '/';

  const rawSearch = useSearch({ strict: false, structuralSharing: false }) as Record<
    string,
    unknown
  >;
  // Whether the current route carries any shared filter params of its own
  // (e.g. we're on /media-units or /map). False on the media-unit detail/edit
  // routes, whose only relevant search param is the opaque `catalogSearch`.
  // Computed once and reused by both memos below instead of each recomputing
  // `pickSharedFilterSearch(rawSearch)` independently.
  const hasDirectSharedFilters = React.useMemo(
    () => Object.keys(pickSharedFilterSearch(rawSearch)).length > 0,
    [rawSearch],
  );

  // The media-unit detail/edit routes don't carry the catalog's shared filter
  // fields (region, formatCode, ...) as their own top-level search params —
  // only their own `tab`/`focus`/`catalogSearch`. When on one of those routes,
  // fall back to decoding `catalogSearch` (see `catalog-return-context.ts`) so
  // the "Mapa" sidebar link still restores the map-shared filter subset,
  // instead of always landing on an unfiltered map. CU-86cajbuug. The "Katalog
  // nośników" link uses `mediaUnitsNavSearch` below instead of this value, for
  // a full (not narrowed) restore — see CU-86cb1nq9n.
  const sharedFilterSearch = React.useMemo(() => {
    const direct = pickSharedFilterSearch(rawSearch);
    const catalogSearchParam = rawSearch['catalogSearch'];
    const decoded =
      typeof catalogSearchParam === 'string' ? decodeCatalogSearch(catalogSearchParam) : null;
    const filters = hasDirectSharedFilters
      ? direct
      : decoded
        ? pickSharedFilterSearch(decoded as unknown as Record<string, unknown>)
        : direct;
    // addToOfferId (the "adding units to offer X" context) is a scalar, not a
    // string-array "shared filter field" (SHARED_FILTER_FIELD_IDS), so
    // pickSharedFilterSearch never carries it. Forward it separately so this
    // sidebar link preserves the active offer-add context like the in-page
    // catalog back-link does, instead of silently dropping it (QA bug).
    const directAddToOfferId =
      typeof rawSearch['addToOfferId'] === 'string' ? rawSearch['addToOfferId'] : undefined;
    const addToOfferId = directAddToOfferId ?? decoded?.addToOfferId;
    return addToOfferId ? { ...filters, addToOfferId } : filters;
  }, [rawSearch, hasDirectSharedFilters]);

  // Full catalog-state restore for the "Katalog nośników" sidebar link specifically
  // (not "Mapa" — /map has no catalog-only fields to restore). Used only as a
  // fallback when the current route carries no shared filter params of its own,
  // i.e. we're on the media-unit detail/edit route where `catalogSearch` is the
  // only carrier. Mirrors the in-page back-link's `decodeCatalogSearch` exactly
  // (full state: free-text search, sort, catalog-only filters — not just the
  // /map-shared subset) so the sidebar link stays consistent with the in-page
  // back-link instead of silently dropping non-shared catalog state (CU-86cb1nq9n).
  // When the current route DOES carry its own shared filter params directly (e.g.
  // already on /media-units), those direct params win, same as sharedFilterSearch.
  const mediaUnitsNavSearch = React.useMemo(() => {
    if (hasDirectSharedFilters) return sharedFilterSearch;
    const catalogSearchParam = rawSearch['catalogSearch'];
    if (typeof catalogSearchParam !== 'string') return sharedFilterSearch;
    return decodeCatalogSearch(catalogSearchParam) as unknown as Record<string, unknown>;
  }, [rawSearch, sharedFilterSearch, hasDirectSharedFilters]);

  // "Mapa" sidebar link additionally restores the persisted map viewport
  // (`viewBbox`), on top of `sharedFilterSearch`'s filters — either directly
  // from the current URL (e.g. panning /map itself) or decoded from
  // `catalogSearch` on the unit detail/edit route, reached via a map pin
  // popup's "Szczegóły" navigation (`map-pin-popup-card.tsx`). `viewBbox` is
  // /map-only and must NOT leak onto the "Katalog nośników" link, which is
  // why it's computed here rather than folded into `sharedFilterSearch`
  // (shared by both links). CU-86cb1nq31.
  const mapNavSearch = React.useMemo(() => {
    const directViewBbox = extractViewBbox(rawSearch);
    const catalogSearchParam = rawSearch['catalogSearch'];
    const decodedViewBbox =
      typeof catalogSearchParam === 'string'
        ? decodeCatalogSearchViewBbox(catalogSearchParam)
        : undefined;
    const viewBbox = directViewBbox ?? decodedViewBbox;
    return viewBbox ? { ...sharedFilterSearch, viewBbox } : sharedFilterSearch;
  }, [rawSearch, sharedFilterSearch]);

  const navSearch = (path: string) =>
    path === '/media-units'
      ? mediaUnitsNavSearch
      : path === '/map'
        ? mapNavSearch
        : FILTER_CARRY_PATHS.has(path)
          ? sharedFilterSearch
          : undefined;

  return (
    <AppFrame
      pageTitle={pageTitle}
      activePath={activePath}
      collapsed={collapsed}
      onToggleCollapsed={() => setCollapsed((c) => !c)}
      access={access}
      userName={auth.user?.name}
      userEmail={auth.user?.email}
      onLogout={() => {
        void auth.logout().then(() => navigate({ to: '/login' }));
      }}
      onChangeLanguage={changeLanguage}
      notificationBell={<NotificationBell />}
      renderNavLink={(item, linkProps) => (
        <Link to={item.path} search={navSearch(item.path)} {...linkProps} />
      )}
      mainStyle={MAIN_STYLE}
    >
      {children}
    </AppFrame>
  );
}
